/**
 * Icon 24 mặt hàng charforge (SP01–SP24), 48×48, vẽ bằng code.
 *
 * Mỗi món = nền theo nhóm hàng + hình biểu tượng đơn giản, tone cozy.
 * Nền theo nhóm: Đồ uống (xanh), Đồ khô (cam), Đồ tươi (xanh lá), Gia dụng (tím).
 */
import {
  BANG_MAU,
  Raster,
  duongThang,
  rect,
  sangLen,
  toiDi,
  tron,
  tronVien,
  vien,
  type VeSprite,
} from '../raster.js';

/** Nền icon theo nhóm hàng: viền + highlight trên. */
function nenMon(r: Raster, mau: string): void {
  r.nen(mau);
  vien(r, 1, 1, 46, 46, toiDi(mau, 0.82), 2);
  rect(r, 4, 4, 40, 3, sangLen(mau, 0.25));
}

const NEN = {
  uong: '#a8d0f0',
  kho: '#f0c890',
  tuoi: '#a8e0a8',
  giaDung: '#d0b8e8',
} as const;

/** Chai nước: thân + nắp + vạch nước. */
function chai(r: Raster, cx: number, y0: number, h: number, mauThan: string, mauNap: string): void {
  const w = 12;
  rect(r, cx - w / 2, y0, w, h, mauThan);
  rect(r, cx - w / 2, y0, 3, h, sangLen(mauThan, 0.3)); // Bóng sáng.
  rect(r, cx - w / 2 + w - 3, y0, 3, h, toiDi(mauThan, 0.8)); // Bóng tối.
  rect(r, cx - 3, y0 - 7, 6, 7, mauThan); // Cổ chai.
  rect(r, cx - 4, y0 - 11, 8, 5, mauNap); // Nắp.
  // Nhãn.
  rect(r, cx - w / 2, y0 + Math.floor(h / 2) - 3, w, 7, BANG_MAU.trang);
}

/** Lon nước: hình trụ + nắp bật. */
function lon(r: Raster, cx: number, y0: number, mau: string): void {
  const w = 14;
  const h = 22;
  rect(r, cx - w / 2, y0, w, h, mau);
  rect(r, cx - w / 2, y0, 3, h, sangLen(mau, 0.3));
  rect(r, cx + w / 2 - 3, y0, 3, h, toiDi(mau, 0.8));
  rect(r, cx - w / 2 + 1, y0 - 2, w - 2, 3, '#cfcfcf');
  rect(r, cx - w / 2 + 1, y0 + h - 1, w - 2, 3, '#9a9a9a');
  // Nắp bật.
  tron(r, cx, y0, 2, '#8a8a8a');
}

/** 24 icon món: id manifest → hàm vẽ. */
export const VE_MON: Record<string, VeSprite> = {
  // --- Đồ uống ---
  'mon-sp01': (r) => {
    // Nước suối.
    nenMon(r, NEN.uong);
    chai(r, 24, 16, 24, '#bfe0f5', '#3f7fbf');
    rect(r, 20, 26, 8, 6, '#3f7fbf'); // Vạch nước.
  },
  'mon-sp02': (r) => {
    // Nước ngọt.
    nenMon(r, NEN.uong);
    lon(r, 24, 16, '#d94f4f');
    rect(r, 18, 24, 12, 5, BANG_MAU.trang);
  },
  'mon-sp03': (r) => {
    // Sữa tươi: hộp giấy.
    nenMon(r, NEN.uong);
    rect(r, 17, 16, 14, 24, BANG_MAU.trang);
    rect(r, 17, 16, 14, 6, '#7fb3e8'); // Mái hộp.
    duongThang(r, 17, 16, 24, 11, '#7fb3e8');
    duongThang(r, 31, 16, 24, 11, '#7fb3e8');
    rect(r, 17, 16, 3, 24, sangLen(BANG_MAU.trang, 0.2));
    rect(r, 28, 16, 3, 24, toiDi(BANG_MAU.trang, 0.85));
    tron(r, 24, 30, 4, '#7fb3e8'); // Giọt sữa.
  },
  'mon-sp04': (r) => {
    // Trà đóng chai.
    nenMon(r, NEN.uong);
    chai(r, 24, 16, 24, '#cfe8b8', '#3fae6a');
    rect(r, 19, 25, 10, 7, '#3fae6a'); // Lá trà trên nhãn.
    duongThang(r, 21, 28, 27, 28, '#1e6e3e');
  },
  'mon-sp05': (r) => {
    // Nước dừa.
    nenMon(r, NEN.uong);
    tron(r, 24, 28, 11, '#6b4a2a');
    tron(r, 24, 28, 8, '#8a5f36');
    rect(r, 20, 14, 8, 6, '#3a2a18'); // Miệng dừa.
    duongThang(r, 24, 16, 32, 6, '#d94f4f'); // Ống hút.
    duongThang(r, 32, 6, 35, 6, '#d94f4f');
    r.px(21, 26, BANG_MAU.trang);
    r.px(22, 25, BANG_MAU.trang);
  },
  'mon-sp06': (r) => {
    // Nước tăng lực.
    nenMon(r, NEN.uong);
    lon(r, 24, 16, '#2e2e2e');
    duongThang(r, 18, 34, 30, 20, '#ffe45e'); // Tia chớp.
    duongThang(r, 30, 20, 26, 20, '#ffe45e');
    duongThang(r, 26, 20, 30, 32, '#ffe45e');
  },

  // --- Đồ khô ---
  'mon-sp07': (r) => {
    // Mì gói: ly mì + đũa.
    nenMon(r, NEN.kho);
    rect(r, 15, 24, 18, 14, '#e86a2a'); // Ly.
    rect(r, 15, 24, 18, 4, '#f09a5e'); // Miệng ly.
    rect(r, 15, 24, 4, 14, sangLen('#e86a2a', 0.2));
    // Sợi mì.
    for (let i = 0; i < 4; i++) {
      duongThang(r, 17 + i * 4, 24, 19 + i * 4, 20, '#ffe45e');
    }
    // Đũa.
    duongThang(r, 28, 22, 36, 8, '#8a5a2e');
    duongThang(r, 31, 22, 38, 10, '#8a5a2e');
  },
  'mon-sp08': (r) => {
    // Bánh quy: hộp + bánh tròn.
    nenMon(r, NEN.kho);
    rect(r, 14, 18, 20, 20, '#c9a25e'); // Hộp.
    rect(r, 14, 18, 20, 6, '#a8832f');
    rect(r, 14, 18, 4, 20, sangLen('#c9a25e', 0.2));
    tron(r, 24, 34, 7, '#e8b86a'); // Bánh.
    tronVien(r, 24, 34, 7, '#a8703a', 1);
    r.px(22, 33, '#6b4a2a');
    r.px(26, 35, '#6b4a2a');
    r.px(24, 32, '#6b4a2a');
  },
  'mon-sp09': (r) => {
    // Gạo (1 kg): bao gạo.
    nenMon(r, NEN.kho);
    rect(r, 16, 18, 16, 20, '#efe8d8');
    rect(r, 16, 18, 16, 5, '#d9cdb2'); // Miệng bao buộc.
    rect(r, 22, 14, 4, 5, '#b89a5a');
    rect(r, 16, 18, 4, 20, sangLen('#efe8d8', 0.15));
    rect(r, 28, 18, 4, 20, toiDi('#efe8d8', 0.88));
    rect(r, 19, 26, 10, 7, '#c96a3b'); // Nhãn.
    rect(r, 21, 28, 6, 3, BANG_MAU.trang);
  },
  'mon-sp10': (r) => {
    // Đồ hộp cá.
    nenMon(r, NEN.kho);
    rect(r, 13, 20, 22, 16, '#b8c8d8'); // Hộp thiếc.
    rect(r, 13, 20, 22, 4, '#d8e4ee');
    rect(r, 13, 32, 22, 4, '#9aaabb');
    rect(r, 13, 20, 4, 16, sangLen('#b8c8d8', 0.2));
    // Con cá trên nhãn.
    tron(r, 24, 28, 4, '#3f7fbf');
    duongThang(r, 28, 28, 33, 24, '#3f7fbf');
    duongThang(r, 28, 28, 33, 32, '#3f7fbf');
    r.px(22, 27, BANG_MAU.den);
  },
  'mon-sp11': (r) => {
    // Kẹo.
    nenMon(r, NEN.kho);
    tron(r, 24, 28, 8, '#d94f9f');
    tron(r, 24, 28, 5, '#e87ab5');
    // Giấy gói hai đầu.
    duongThang(r, 16, 28, 8, 22, '#b34a85');
    duongThang(r, 16, 28, 8, 34, '#b34a85');
    duongThang(r, 32, 28, 40, 22, '#b34a85');
    duongThang(r, 32, 28, 40, 34, '#b34a85');
    rect(r, 21, 24, 6, 3, BANG_MAU.trang);
  },
  'mon-sp12': (r) => {
    // Bánh tráng: xấp bánh tròn.
    nenMon(r, NEN.kho);
    for (let i = 0; i < 4; i++) {
      tron(r, 24, 30 - i * 2, 10, i % 2 === 0 ? '#f5edd8' : '#e8dcc0');
    }
    tronVien(r, 24, 24, 10, '#c9b88e', 1);
    rect(r, 14, 36, 20, 5, '#c96a3b'); // Bao bì.
  },

  // --- Đồ tươi ---
  'mon-sp13': (r) => {
    // Rau cải.
    nenMon(r, NEN.tuoi);
    rect(r, 22, 30, 4, 10, '#e8f5e8'); // Cuống.
    for (let i = 0; i < 5; i++) {
      const x = 14 + i * 5;
      tron(r, x, 24 - (i % 2) * 3, 5, i % 2 === 0 ? '#3fae6a' : '#58c878');
    }
    duongThang(r, 24, 20, 24, 30, '#2a8e54');
  },
  'mon-sp14': (r) => {
    // Trứng gà: vỉ trứng.
    nenMon(r, NEN.tuoi);
    rect(r, 13, 30, 22, 8, '#c9a25e'); // Vỉ.
    for (let i = 0; i < 3; i++) {
      tron(r, 18 + i * 6, 28, 4, '#f5edd8');
      r.px(17 + i * 6, 27, BANG_MAU.trang);
    }
    rect(r, 13, 30, 22, 2, '#a8832f');
  },
  'mon-sp15': (r) => {
    // Thịt heo (kg).
    nenMon(r, NEN.tuoi);
    rect(r, 12, 26, 24, 10, '#e87a7a'); // Miếng thịt.
    rect(r, 12, 26, 24, 3, sangLen('#e87a7a', 0.25));
    rect(r, 15, 30, 10, 4, '#f5c8c8'); // Mỡ.
    rect(r, 28, 32, 6, 2, '#f5c8c8');
    // Khay.
    rect(r, 10, 36, 28, 3, '#9fd0f0');
  },
  'mon-sp16': (r) => {
    // Cá tươi (kg).
    nenMon(r, NEN.tuoi);
    tron(r, 22, 28, 7, '#7fb3d9'); // Thân.
    duongThang(r, 29, 28, 37, 21, '#7fb3d9'); // Đuôi.
    duongThang(r, 29, 28, 37, 35, '#7fb3d9');
    duongThang(r, 37, 21, 37, 35, '#5e93b9');
    duongThang(r, 20, 22, 24, 18, '#5e93b9'); // Vây.
    r.px(18, 27, BANG_MAU.den);
    for (let i = 0; i < 3; i++) r.px(22 + i * 3, 30, '#5e93b9'); // Vảy.
    // Khay đá.
    rect(r, 10, 37, 28, 3, '#d8ecf8');
  },
  'mon-sp17': (r) => {
    // Đậu hũ.
    nenMon(r, NEN.tuoi);
    rect(r, 14, 24, 20, 14, '#f5f0e0');
    rect(r, 14, 24, 20, 3, BANG_MAU.trang);
    rect(r, 14, 24, 4, 14, sangLen('#f5f0e0', 0.15));
    rect(r, 30, 24, 4, 14, toiDi('#f5f0e0', 0.9));
    r.px(22, 30, '#e0d6b8');
    r.px(27, 33, '#e0d6b8');
  },
  'mon-sp18': (r) => {
    // Chuối: nải chuối.
    nenMon(r, NEN.tuoi);
    rect(r, 21, 12, 6, 5, '#6b4a2a'); // Cuống nải.
    for (let i = 0; i < 4; i++) {
      const x = 15 + i * 6;
      // Quả chuối cong (3 đoạn).
      rect(r, x, 18, 4, 10, '#ffe45e');
      rect(r, x + (i % 2 === 0 ? -2 : 2), 28, 4, 6, '#f0d040');
      rect(r, x, 18, 1, 10, sangLen('#ffe45e', 0.3));
    }
  },

  // --- Gia dụng ---
  'mon-sp19': (r) => {
    // Xà phòng.
    nenMon(r, NEN.giaDung);
    rect(r, 13, 24, 22, 12, '#f0e8f5');
    rect(r, 13, 24, 22, 3, BANG_MAU.trang);
    rect(r, 13, 24, 4, 12, sangLen('#f0e8f5', 0.15));
    rect(r, 31, 24, 4, 12, toiDi('#f0e8f5', 0.88));
    tron(r, 19, 20, 3, '#d0e8f8'); // Bọt.
    tron(r, 28, 18, 4, '#d0e8f8');
    tron(r, 32, 22, 2, '#d0e8f8');
  },
  'mon-sp20': (r) => {
    // Khăn lau: khăn gấp.
    nenMon(r, NEN.giaDung);
    rect(r, 12, 22, 24, 6, '#7fd6d6');
    rect(r, 12, 28, 24, 6, '#5fb8b8');
    rect(r, 12, 34, 24, 4, '#4aa0a0');
    rect(r, 12, 22, 5, 16, sangLen('#7fd6d6', 0.25));
    for (let i = 0; i < 3; i++) duongThang(r, 14 + i * 8, 24, 14 + i * 8, 36, '#3f9090');
  },
  'mon-sp21': (r) => {
    // Pin AA: 2 viên pin.
    nenMon(r, NEN.giaDung);
    for (const [x, mau] of [
      [16, '#3f7fbf'],
      [28, '#d94f4f'],
    ] as const) {
      rect(r, x, 16, 8, 20, mau);
      rect(r, x, 16, 8, 4, sangLen(mau, 0.25));
      rect(r, x + 2, 12, 4, 4, '#8a8a8a'); // Đầu pin.
      rect(r, x, 24, 8, 6, BANG_MAU.trang); // Nhãn.
      duongThang(r, x + 2, 27, x + 6, 27, mau);
    }
  },
  'mon-sp22': (r) => {
    // Kem đánh răng: tuýp kem.
    nenMon(r, NEN.giaDung);
    rect(r, 18, 16, 12, 20, '#4f9fd9'); // Thân tuýp.
    rect(r, 18, 16, 3, 20, sangLen('#4f9fd9', 0.3));
    rect(r, 27, 16, 3, 20, toiDi('#4f9fd9', 0.8));
    rect(r, 21, 10, 6, 6, '#2e6ea5'); // Cổ tuýp.
    rect(r, 20, 6, 8, 4, '#1f4a6e'); // Nắp.
    rect(r, 18, 36, 12, 3, '#2e6ea5'); // Đáy gấp.
    rect(r, 20, 24, 8, 6, BANG_MAU.trang); // Nhãn.
  },
  'mon-sp23': (r) => {
    // Dầu gội: chai có vòi nhấn.
    nenMon(r, NEN.giaDung);
    rect(r, 17, 20, 14, 18, '#9b59b6');
    rect(r, 17, 20, 4, 18, sangLen('#9b59b6', 0.3));
    rect(r, 27, 20, 4, 18, toiDi('#9b59b6', 0.8));
    rect(r, 21, 12, 6, 8, '#7a3f8e'); // Cổ.
    rect(r, 21, 8, 12, 4, '#7a3f8e'); // Vòi nhấn.
    rect(r, 29, 12, 4, 6, '#5e2f6e'); // Đầu vòi.
    tron(r, 24, 30, 3, BANG_MAU.trang); // Bọt trên nhãn.
  },
  'mon-sp24': (r) => {
    // Túi nilon.
    nenMon(r, NEN.giaDung);
    rect(r, 15, 18, 18, 18, '#e8f0f5');
    rect(r, 15, 18, 18, 4, '#d0dce8'); // Miệng túi.
    rect(r, 15, 18, 4, 18, sangLen('#e8f0f5', 0.2));
    rect(r, 29, 18, 4, 18, toiDi('#e8f0f5', 0.85));
    // Quai xách.
    duongThang(r, 18, 18, 18, 10, '#b8c8d8');
    duongThang(r, 30, 18, 30, 10, '#b8c8d8');
    rect(r, 16, 8, 16, 3, '#b8c8d8');
    // Nếp gấp.
    duongThang(r, 20, 24, 24, 34, '#c8d8e8');
    duongThang(r, 28, 24, 25, 34, '#c8d8e8');
  },
};
